import React, { useCallback, useEffect, useState } from "react";
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Grid,
	DialogContentText,
	Select,
	MenuItem,
	TextField,
	FormControlLabel,
	Switch,
} from "@mui/material";
import supabase from "../../supabaseClient";
import axios from "axios";
import useAdminAuth from "../../utils/useAdminAuth";
import AuthGate from "../../Components/AuthGate";

const QUESTION_TYPES = [
	{ value: "text", label: "Short Answer" },
	{ value: "textarea", label: "Long Answer" },
	{ value: "email", label: "Email" },
	{ value: "tel", label: "Phone" },
	{ value: "date", label: "Date" },
];

const createQuestion = () => ({
	id: `question-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	label: "",
	type: "text",
	required: false,
});

const createPostingForm = () => ({
	position: "",
	description: "",
	open_for_apps: true,
	display_order: 0,
	questions: [],
});

const normalizeQuestions = (questions = []) =>
	questions.map((question) => ({
		id: question.id || createQuestion().id,
		label: question.label || "",
		type: question.type || "text",
		required: Boolean(question.required),
	}));

export default function AdminJobApplications() {
	const { authChecked, isLoggedIn, isAdmin } = useAdminAuth();

	const [loading, setLoading] = useState(true);
	const [applications, setApplications] = useState([]);
	const [selectedApp, setSelectedApp] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [rejectingApp, setRejectingApp] = useState(false);
	const [processingReject, setProcessingReject] = useState(false);
	const [statusFilter, setStatusFilter] = useState("submitted");
	const [postingsLoading, setPostingsLoading] = useState(true);
	const [postings, setPostings] = useState([]);
	const [postingDialogOpen, setPostingDialogOpen] = useState(false);
	const [editingPosting, setEditingPosting] = useState(null);
	const [savingPosting, setSavingPosting] = useState(false);
	const [updatingPostingId, setUpdatingPostingId] = useState(null);
	const [postingForm, setPostingForm] = useState(createPostingForm());

	const fetchApplications = useCallback(async () => {
		setLoading(true);
		let query = supabase
			.from("job_applications")
			.select("*")
			.order("created_at", { ascending: false });

		if (statusFilter && statusFilter !== "all") {
			query = query.eq("status", statusFilter);
		}

		const { data, error } = await query;
		if (error) {
			console.error("Error fetching applications:", error);
			setApplications([]);
		} else {
			setApplications(data || []);
		}
		setLoading(false);
	}, [statusFilter]);

	const fetchPostings = useCallback(async () => {
		setPostingsLoading(true);
		const { data, error } = await supabase
			.from("job_application_postings")
			.select("*")
			.order("display_order", { ascending: true })
			.order("created_at", { ascending: true });

		if (error) {
			console.error("Error fetching postings:", error);
			setPostings([]);
		} else {
			setPostings(data || []);
		}
		setPostingsLoading(false);
	}, []);

	useEffect(() => {
		fetchApplications();
	}, [fetchApplications]);

	useEffect(() => {
		fetchPostings();
	}, [fetchPostings]);

	if (!authChecked) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}
	if (!isLoggedIn) return <AuthGate variant="not-logged-in" />;
	if (!isAdmin) return <AuthGate variant="unauthorized" />;

	const handleView = (app) => {
		setSelectedApp(app);
		setRejectingApp(false);
		setModalOpen(true);
	};

	const handleClose = () => {
		setSelectedApp(null);
		setRejectingApp(false);
		setModalOpen(false);
	};

	const handleRejectClick = () => {
		setRejectingApp(true);
	};

	const handleRejectCancel = () => {
		setRejectingApp(false);
	};

	const handleRejectConfirm = async () => {
		if (!selectedApp) return;
		setProcessingReject(true);

		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			alert("You must be logged in to perform this action.");
			setProcessingReject(false);
			return;
		}

		try {
			const res = await axios.post(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/close-job-application`,
				{
					id: selectedApp.id,
				},
				{
					headers: {
						Authorization: "Bearer " + session.access_token,
					},
				},
			);

			const data = res.data;
			if (res.status !== 200) {
				throw new Error(data.error || "Failed to close application");
			}

			setApplications((prev) =>
				prev.map((application) =>
					application.id === selectedApp.id
						? { ...application, status: "closed" }
						: application,
				),
			);

			setRejectingApp(false);
			setModalOpen(false);
			setSelectedApp(null);
		} catch (err) {
			console.error(err);
			alert("Error rejecting applicant: " + err.message);
		} finally {
			setProcessingReject(false);
		}
	};

	const handleMarkUnderReviewClick = async () => {
		if (!selectedApp) return;

		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			alert("You must be logged in to perform this action.");
			return;
		}

		try {
			const { error } = await supabase
				.from("job_applications")
				.update({ status: "under_review" })
				.eq("id", selectedApp.id);

			if (error) throw error;

			setApplications((prev) =>
				prev.map((application) =>
					application.id === selectedApp.id
						? { ...application, status: "under_review" }
						: application,
				),
			);

			setSelectedApp((prev) => ({ ...prev, status: "under_review" }));
		} catch (err) {
			console.error(err);
			alert("Error updating application status: " + err.message);
		}
	};

	const handlePostingDialogOpen = (posting = null) => {
		setEditingPosting(posting);
		setPostingForm(
			posting
				? {
						position: posting.position || "",
						description: posting.description || "",
						open_for_apps: posting.open_for_apps ?? true,
						display_order: posting.display_order ?? 0,
						questions: normalizeQuestions(posting.questions || []),
					}
				: createPostingForm(),
		);
		setPostingDialogOpen(true);
	};

	const handlePostingDialogClose = () => {
		setPostingDialogOpen(false);
		setEditingPosting(null);
		setPostingForm(createPostingForm());
	};

	const handlePostingFieldChange = (e) => {
		const { name, value } = e.target;
		setPostingForm((prev) => ({
			...prev,
			[name]:
				name === "display_order"
					? Number.parseInt(value || "0", 10) || 0
					: value,
		}));
	};

	const handlePostingToggle = (_, checked) => {
		setPostingForm((prev) => ({ ...prev, open_for_apps: checked }));
	};

	const handleQuestionChange = (index, field, value) => {
		setPostingForm((prev) => {
			const questions = [...prev.questions];
			questions[index] = {
				...questions[index],
				[field]: field === "required" ? value : value,
			};
			return { ...prev, questions };
		});
	};

	const handleAddQuestion = () => {
		setPostingForm((prev) => ({
			...prev,
			questions: [...prev.questions, createQuestion()],
		}));
	};

	const handleRemoveQuestion = (index) => {
		setPostingForm((prev) => ({
			...prev,
			questions: prev.questions.filter(
				(_, questionIndex) => questionIndex !== index,
			),
		}));
	};

	const handlePostingSave = async () => {
		if (!postingForm.position.trim()) {
			alert("Please enter a posting title.");
			return;
		}

		if (postingForm.questions.some((question) => !question.label.trim())) {
			alert("Each question must include a prompt.");
			return;
		}

		setSavingPosting(true);

		const payload = {
			position: postingForm.position.trim(),
			description: postingForm.description,
			open_for_apps: postingForm.open_for_apps,
			display_order: postingForm.display_order || 0,
			questions: postingForm.questions.map((question) => ({
				id: question.id,
				label: question.label.trim(),
				type: question.type,
				required: question.required,
			})),
			updated_at: new Date().toISOString(),
		};

		const result = editingPosting
			? await supabase
					.from("job_application_postings")
					.update(payload)
					.eq("id", editingPosting.id)
			: await supabase.from("job_application_postings").insert([payload]);

		if (result.error) {
			alert("Error saving posting: " + result.error.message);
		} else {
			await fetchPostings();
			handlePostingDialogClose();
		}

		setSavingPosting(false);
	};

	const handlePostingDelete = async (posting) => {
		if (
			!window.confirm(
				`Delete the "${posting.position}" posting? Existing applications will be kept.`,
			)
		) {
			return;
		}

		const result = await supabase
			.from("job_application_postings")
			.delete()
			.eq("id", posting.id);

		if (result.error) {
			alert("Error deleting posting: " + result.error.message);
			return;
		}

		await fetchPostings();
	};

	const handlePostingStatusToggle = async (posting, checked) => {
		setUpdatingPostingId(posting.id);
		const { error } = await supabase
			.from("job_application_postings")
			.update({
				open_for_apps: checked,
				updated_at: new Date().toISOString(),
			})
			.eq("id", posting.id);

		if (error) {
			alert("Error updating posting status: " + error.message);
		} else {
			setPostings((prev) =>
				prev.map((currentPosting) =>
					currentPosting.id === posting.id
						? { ...currentPosting, open_for_apps: checked }
						: currentPosting,
				),
			);
		}

		setUpdatingPostingId(null);
	};

	return (
		<Box sx={{ mx: "auto" }}>
			<Typography variant="h5" gutterBottom>
				Job Postings
			</Typography>
			<Box mb={2}>
				<Button variant="contained" onClick={() => handlePostingDialogOpen()}>
					Add Posting
				</Button>
			</Box>
			{postingsLoading ? (
				<CircularProgress />
			) : postings.length === 0 ? (
				<Typography sx={{ mb: 4 }}>No job postings configured yet.</Typography>
			) : (
				<TableContainer component={Paper} sx={{ mb: 4 }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Position</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Questions</TableCell>
								<TableCell>Display Order</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{postings.map((posting) => (
								<TableRow key={posting.id}>
									<TableCell>{posting.position}</TableCell>
									<TableCell>
										<FormControlLabel
											control={
												<Switch
													checked={posting.open_for_apps}
													onChange={(_, checked) =>
														handlePostingStatusToggle(posting, checked)
													}
													disabled={updatingPostingId === posting.id}
												/>
											}
											label={posting.open_for_apps ? "Open" : "Closed"}
										/>
									</TableCell>
									<TableCell>{posting.questions?.length || 0}</TableCell>
									<TableCell>{posting.display_order || 0}</TableCell>
									<TableCell>
										<Button
											size="small"
											onClick={() => handlePostingDialogOpen(posting)}
										>
											Edit
										</Button>
										<Button
											size="small"
											color="error"
											onClick={() => handlePostingDelete(posting)}
										>
											Delete
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Typography variant="h5" gutterBottom>
				Job Applications
			</Typography>

			<Box mb={2}>
				<Select
					value={statusFilter}
					onChange={(e) => setStatusFilter(e.target.value)}
					size="small"
				>
					<MenuItem value="all">All</MenuItem>
					<MenuItem value="submitted">Submitted</MenuItem>
					<MenuItem value="under_review">Under Review</MenuItem>
					<MenuItem value="closed">Closed</MenuItem>
				</Select>
			</Box>

			{loading ? (
				<CircularProgress />
			) : applications.length === 0 ? (
				<Typography>No applications yet.</Typography>
			) : (
				<TableContainer component={Paper}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Position</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Submitted At</TableCell>
								<TableCell>Action</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{applications.map((app) => (
								<TableRow key={app.id}>
									<TableCell>{app.position}</TableCell>
									<TableCell>{app.status}</TableCell>
									<TableCell>{app.name}</TableCell>
									<TableCell>
										{new Date(app.created_at).toLocaleString()}
									</TableCell>
									<TableCell>
										<Button
											variant="outlined"
											size="small"
											onClick={() => handleView(app)}
										>
											View Details
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Dialog
				open={postingDialogOpen}
				onClose={handlePostingDialogClose}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					{editingPosting ? "Edit Job Posting" : "Add Job Posting"}
				</DialogTitle>
				<DialogContent dividers>
					<TextField
						label="Position Title"
						name="position"
						value={postingForm.position}
						onChange={handlePostingFieldChange}
						fullWidth
						margin="normal"
					/>
					<TextField
						label="Description"
						name="description"
						value={postingForm.description}
						onChange={handlePostingFieldChange}
						fullWidth
						margin="normal"
						multiline
						minRows={6}
					/>
					<TextField
						label="Display Order"
						name="display_order"
						type="number"
						value={postingForm.display_order}
						onChange={handlePostingFieldChange}
						fullWidth
						margin="normal"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={postingForm.open_for_apps}
								onChange={handlePostingToggle}
							/>
						}
						label="Open for applications"
					/>
					<Box mt={2}>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							mb={1}
						>
							<Typography variant="h6">Questions</Typography>
							<Button variant="outlined" onClick={handleAddQuestion}>
								Add Question
							</Button>
						</Box>
						{postingForm.questions.length === 0 ? (
							<Typography color="text.secondary">
								Applicants will still be asked for their name, email, and phone.
							</Typography>
						) : (
							postingForm.questions.map((question, index) => (
								<Box
									key={question.id}
									sx={{
										border: "1px solid #ddd",
										borderRadius: 1,
										p: 2,
										mb: 2,
									}}
								>
									<Grid container spacing={2}>
										<Grid item xs={12} md={7}>
											<TextField
												label={`Question ${index + 1}`}
												value={question.label}
												onChange={(e) =>
													handleQuestionChange(index, "label", e.target.value)
												}
												fullWidth
											/>
										</Grid>
										<Grid item xs={12} md={3}>
											<Select
												value={question.type}
												onChange={(e) =>
													handleQuestionChange(index, "type", e.target.value)
												}
												fullWidth
											>
												{QUESTION_TYPES.map((type) => (
													<MenuItem key={type.value} value={type.value}>
														{type.label}
													</MenuItem>
												))}
											</Select>
										</Grid>
										<Grid item xs={12} md={2}>
											<Button
												color="error"
												fullWidth
												onClick={() => handleRemoveQuestion(index)}
											>
												Remove
											</Button>
										</Grid>
										<Grid item xs={12}>
											<FormControlLabel
												control={
													<Switch
														checked={question.required}
														onChange={(_, checked) =>
															handleQuestionChange(index, "required", checked)
														}
													/>
												}
												label="Required question"
											/>
										</Grid>
									</Grid>
								</Box>
							))
						)}
						<Box mt={2}>
							<Button variant="outlined" onClick={handleAddQuestion}>
								{postingForm.questions.length === 0
									? "Add First Question"
									: "Add Another Question"}
							</Button>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handlePostingDialogClose} disabled={savingPosting}>
						Cancel
					</Button>
					<Button
						onClick={handlePostingSave}
						variant="contained"
						disabled={savingPosting}
					>
						{savingPosting ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={modalOpen} onClose={handleClose} maxWidth="md" fullWidth>
				<DialogTitle>Application Details</DialogTitle>
				<DialogContent dividers>
					{selectedApp && !rejectingApp && (
						<Box>
							<Grid container spacing={2} mb={2}>
								<Grid item xs={12} sm={6}>
									<Typography variant="h5" gutterBottom>
										Name: {selectedApp.name}
									</Typography>
									<Typography variant="subtitle1" gutterBottom>
										Email: {selectedApp.email}
									</Typography>
									<Typography variant="subtitle1" gutterBottom>
										Phone: {selectedApp.phone}
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Typography variant="subtitle1" gutterBottom>
										{selectedApp.position}
									</Typography>
									<Typography variant="subtitle2" gutterBottom>
										Submitted At:{" "}
										{new Date(selectedApp.created_at).toLocaleString()}
									</Typography>
									<Typography variant="subtitle2" gutterBottom>
										Status: {selectedApp.status}
									</Typography>
								</Grid>
							</Grid>
							<Grid container spacing={2}>
								{selectedApp.answers &&
									Object.entries(selectedApp.answers).map(([key, value]) => (
										<Grid item xs={12} sm={6} key={key}>
											<Typography variant="subtitle2" color="text.secondary">
												{key}
											</Typography>
											<Typography variant="body2" sx={{ mb: 1 }}>
												{value}
											</Typography>
										</Grid>
									))}
							</Grid>
						</Box>
					)}
					{selectedApp && rejectingApp && (
						<Box textAlign="center" py={4}>
							<DialogContentText sx={{ mb: 2 }}>
								Confirm rejecting the application for:
							</DialogContentText>
							<Typography variant="h6">{selectedApp.name}</Typography>
							<Typography variant="subtitle1" gutterBottom>
								Position: {selectedApp.position}
							</Typography>
							<DialogContentText>
								This will mark the application as closed and send the applicant
								a rejection email.
							</DialogContentText>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					{!rejectingApp ? (
						<>
							<Button onClick={handleClose}>Close</Button>
							{selectedApp && selectedApp.status === "submitted" && (
								<Button
									variant="outlined"
									color="primary"
									onClick={handleMarkUnderReviewClick}
								>
									Mark as Under Review
								</Button>
							)}
							{selectedApp && selectedApp.status !== "closed" && (
								<Button
									variant="outlined"
									color="error"
									onClick={handleRejectClick}
								>
									Reject Applicant
								</Button>
							)}
						</>
					) : (
						<>
							<Button onClick={handleRejectCancel} disabled={processingReject}>
								No
							</Button>
							<Button
								variant="contained"
								color="error"
								onClick={handleRejectConfirm}
								disabled={processingReject}
							>
								Yes
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
		</Box>
	);
}
